<section class="feed-balance wrapper--spacing-horizontal">
  <header>
    <h1 class="text--h1">{{ 'feed-balance.title' | translate }}</h1>
    <mat-form-field subscriptSizing="dynamic">
      <mat-label>{{ 'feed-balance.flock' | translate }}</mat-label>
      <mat-select
        [value]="balance()?.flockId"
        (selectionChange)="changeFlock($event)"
        [disabled]="loading() || saving()"
      >
        @for (flock of flocks(); track flock.id) {
          <mat-option [value]="flock.id"
            >{{ flock.name }} · {{ flock.arrival?.barn?.name }}</mat-option
          >
        }
      </mat-select>
    </mat-form-field>
    @if (balance(); as balance) {
      <span data-test="feed-balance-status">
        {{ (balance.savedAt ? 'feed-balance.saved' : 'feed-balance.not-registered') | translate }}
        @if (balance.savedAt) {
          · {{ balance.savedAt | date: 'short' }}
        }
      </span>
    }
  </header>
  @if (loading()) {
    <p role="status">{{ 'feed-balance.loading' | translate }}</p>
  }
  @if (error()) {
    <p role="alert" class="error">{{ error() | translate }}</p>
  }
  @if (saved()) {
    <p role="status">{{ 'feed-balance.save-success' | translate }}</p>
  }
  @if (balance()) {
    <p class="notice">{{ 'feed-balance.correction-help' | translate }}</p>
    <p class="notice">{{ 'feed-balance.period-notice' | translate }}</p>
    <agr-table-v2
      [dataSource]="dataSource"
      [withStickyHeaders]="true"
      customTableDataSectionHeight="auto"
      (filterClearClick)="clearFilters()"
    >
      <ng-container *agrTableTopBar></ng-container>
      <ng-container *agrTableSearch>
        <agr-table-search
          [label]="'feed-balance.search' | translate"
          [placeholder]="'feed-balance.search' | translate"
          [ngModel]="search()"
          (ngModelChange)="search.set($event ?? '')"
          data-test="feed-balance-search"
        />
      </ng-container>
      <ng-container *agrTableFilters>
        <div class="table-v2-filter">
          <mat-form-field appearance="fill" subscriptSizing="dynamic">
            <mat-label>{{ 'feed-balance.period-start' | translate }}</mat-label>
            <input
              matInput
              [matDatepicker]="startDatePicker"
              [ngModel]="pickerPeriod().start"
              (dateChange)="changePickerDate('start', $event.value)"
              (focus)="startDatePicker.open()"
              [disabled]="showAllDates()"
              data-test="feed-balance-period-start"
            />
            <mat-datepicker-toggle matIconSuffix [for]="startDatePicker"></mat-datepicker-toggle>
            <mat-datepicker touchUi #startDatePicker></mat-datepicker>
          </mat-form-field>
        </div>
        <div class="table-v2-filter">
          <mat-form-field appearance="fill" subscriptSizing="dynamic">
            <mat-label>{{ 'feed-balance.period-end' | translate }}</mat-label>
            <input
              matInput
              [matDatepicker]="endDatePicker"
              [ngModel]="pickerPeriod().end"
              (dateChange)="changePickerDate('end', $event.value)"
              (focus)="endDatePicker.open()"
              [disabled]="showAllDates()"
              data-test="feed-balance-period-end"
            />
            <mat-datepicker-toggle matIconSuffix [for]="endDatePicker"></mat-datepicker-toggle>
            <mat-datepicker touchUi #endDatePicker></mat-datepicker>
          </mat-form-field>
        </div>
        <button
          mat-stroked-button
          color="primary"
          class="delivery-visibility-button"
          (click)="showAllDates.set(!showAllDates())"
          [attr.aria-pressed]="showAllDates()"
          data-test="feed-balance-show-all-dates"
        >
          {{
            (showAllDates() ? 'feed-balance.use-flock-period' : 'feed-balance.show-all-dates')
              | translate
          }}
        </button>
      </ng-container>
      <ng-container matColumnDef="deliveryDate">
        <th mat-header-cell *matHeaderCellDef>{{ 'feed-balance.delivery-date' | translate }}</th>
        <td
          mat-cell
          *matCellDef="let row"
          [attr.aria-label]="'feed-balance.delivery-date' | translate"
        >
          {{ row.deliveryDate | date: 'shortDate' }}
        </td>
      </ng-container>
      <ng-container matColumnDef="externalDeliveryId">
        <th mat-header-cell *matHeaderCellDef>{{ 'feed-balance.delivery-number' | translate }}</th>
        <td
          mat-cell
          *matCellDef="let row"
          [attr.aria-label]="'feed-balance.delivery-number' | translate"
        >
          {{ row.externalDeliveryId || '—' }}
        </td>
      </ng-container>
      <ng-container matColumnDef="externalOrderId">
        <th mat-header-cell *matHeaderCellDef>{{ 'feed-balance.order-number' | translate }}</th>
        <td
          mat-cell
          *matCellDef="let row"
          [attr.aria-label]="'feed-balance.order-number' | translate"
        >
          {{ row.externalOrderId || '—' }}
        </td>
      </ng-container>
      <ng-container matColumnDef="feed">
        <th mat-header-cell *matHeaderCellDef>{{ 'feed-balance.feed' | translate }}</th>
        <td mat-cell *matCellDef="let row" [attr.aria-label]="'feed-balance.feed' | translate">
          {{ row.productTitle }} <small class="text--label">{{ row.productCode }}</small>
          @if (isUnavailableAllocation(row)) {
            <strong class="error">{{ 'feed-balance.unavailable-allocation' | translate }}</strong>
          } @else if (row.isMissing) {
            <strong class="error">{{ 'feed-balance.missing-delivery-line' | translate }}</strong>
          } @else if (!row.isActive) {
            <strong>{{ 'feed-balance.inactive' | translate }}</strong>
          }
        </td>
      </ng-container>
      <ng-container matColumnDef="delivered">
        <th mat-header-cell *matHeaderCellDef>{{ 'feed-balance.delivered' | translate }}</th>
        <td mat-cell *matCellDef="let row" [attr.aria-label]="'feed-balance.delivered' | translate">
          <span [attr.data-test]="'feed-delivered-kg-' + row.deliveryLineId">
            @if (row.deliveredKg != null) {
              {{ row.deliveredKg | number: '1.0-3' }} kg
            } @else {
              {{ 'feed-balance.weight-unavailable' | translate }}
            }
          </span>
          @if (
            row.deliveredKg == null ||
            row.deliveredUnit?.toLowerCase() !== 'kg' ||
            row.deliveredQuantity !== row.deliveredKg
          ) {
            <small class="text--label">
              ({{ row.deliveredQuantity | number }} {{ row.deliveredUnit }})
            </small>
          }
        </td>
      </ng-container>
      <ng-container matColumnDef="usedKg">
        <th mat-header-cell *matHeaderCellDef>{{ 'feed-balance.used-kg' | translate }}</th>
        <td mat-cell *matCellDef="let row" [attr.aria-label]="'feed-balance.used-kg' | translate">
          <mat-form-field appearance="fill" subscriptSizing="dynamic">
            <mat-label>{{ 'feed-balance.used-kg-short' | translate }}</mat-label>
            <input
              matInput
              type="number"
              min="0"
              step="0.001"
              [ngModel]="quantityInputValue(row.usedKg)"
              #quantityInput
              (input)="updateQuantityFromInput(row.deliveryLineId, quantityInput)"
              [disabled]="saving()"
              [attr.data-test]="'feed-used-' + row.deliveryLineId"
              [attr.aria-label]="
                ('feed-balance.used-kg' | translate) +
                ': ' +
                row.productTitle +
                ' / ' +
                row.lineNumber
              "
            />
          </mat-form-field>
        </td>
      </ng-container>
      <tr mat-header-row *matHeaderRowDef="columns; sticky: true"></tr>
      <tr mat-row *matRowDef="let row; columns: columns"></tr>
      <tr *matNoDataRow>
        <td [attr.colspan]="columns.length" class="spacing--padding">
          {{ (rows().length ? 'feed-balance.no-matches' : 'feed-balance.empty') | translate }}
        </td>
      </tr>
    </agr-table-v2>
    @if (!valid()) {
      <p role="alert" class="error">{{ 'feed-balance.invalid-input' | translate }}</p>
    }
    <p class="notice">{{ 'feed-balance.calculation-notice' | translate }}</p>
    <footer data-test="feed-balance-actions">
      <strong>{{ 'feed-balance.total' | translate }}: {{ total() | number: '1.0-3' }} kg</strong>
      <button mat-button (click)="cancel()" [disabled]="saving()">
        {{ 'feed-balance.cancel' | translate }}
      </button>
      <button
        mat-flat-button
        color="primary"
        (click)="save()"
        [disabled]="saving() || !valid() || hasUnavailableAllocations()"
        data-test="feed-balance-save"
      >
        {{ (saving() ? 'feed-balance.saving' : 'feed-balance.save') | translate }}
      </button>
    </footer>
  }
  @if (!balance() && !loading()) {
    <button mat-button (click)="cancel()">{{ 'feed-balance.cancel' | translate }}</button>
  }
</section>
